import { Dialog as SheetPrimitive } from '@base-ui/react/dialog';
import { faXmark } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as React from 'react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils/cn';

type SheetSide = 'top' | 'right' | 'bottom' | 'left';

/** Min/max drag bounds. Horizontal sides size by width, vertical by height. */
const RESIZE_MIN = 280;
const clampResize = (value: number, isHorizontal: boolean) => {
  const viewport = isHorizontal ? window.innerWidth : window.innerHeight;
  return Math.min(Math.max(value, RESIZE_MIN), viewport * 0.95);
};

function Sheet({ ...props }: SheetPrimitive.Root.Props) {
  return <SheetPrimitive.Root data-slot='sheet' {...props} />;
}

function SheetTrigger({ ...props }: SheetPrimitive.Trigger.Props) {
  return <SheetPrimitive.Trigger data-slot='sheet-trigger' {...props} />;
}

function SheetClose({ ...props }: SheetPrimitive.Close.Props) {
  return <SheetPrimitive.Close data-slot='sheet-close' {...props} />;
}

function SheetPortal({ ...props }: SheetPrimitive.Portal.Props) {
  return <SheetPrimitive.Portal data-slot='sheet-portal' {...props} />;
}

function SheetOverlay({ className, ...props }: SheetPrimitive.Backdrop.Props) {
  return (
    <SheetPrimitive.Backdrop
      data-slot='sheet-overlay'
      className={cn(
        'fixed inset-0 z-40 bg-black/60 transition-opacity duration-300 ease-[cubic-bezier(0.32,0.72,0,1)] data-ending-style:opacity-0 data-starting-style:opacity-0 supports-backdrop-filter:backdrop-blur-xs',
        className,
      )}
      {...props}
    />
  );
}

function SheetContent({
  className,
  children,
  side = 'right',
  showCloseButton = true,
  resizable = true,
  style,
  ...props
}: SheetPrimitive.Popup.Props & {
  side?: SheetSide;
  showCloseButton?: boolean;
  /** Drag-to-resize the sheet from its inner edge. On by default. */
  resizable?: boolean;
}) {
  const popupRef = React.useRef<HTMLDivElement>(null);
  // `null` until first drag, so the sheet keeps its default class-based size.
  const [size, setSize] = React.useState<number | null>(null);
  const [isDragging, setIsDragging] = React.useState(false);
  const isHorizontal = side === 'left' || side === 'right';

  const handleResizeStart = React.useCallback(
    (event: React.PointerEvent) => {
      const popup = popupRef.current;
      if (!popup) return;
      event.preventDefault();
      const rect = popup.getBoundingClientRect();
      const startPos = isHorizontal ? event.clientX : event.clientY;
      const startSize = isHorizontal ? rect.width : rect.height;
      // Sheets anchored to the far edge (right/bottom) grow as the pointer
      // moves toward the screen centre, i.e. against the axis direction.
      const invert = side === 'right' || side === 'bottom';

      const onMove = (moveEvent: PointerEvent) => {
        const pos = isHorizontal ? moveEvent.clientX : moveEvent.clientY;
        const delta = (pos - startPos) * (invert ? -1 : 1);
        setSize(clampResize(startSize + delta, isHorizontal));
      };
      const onUp = () => {
        setIsDragging(false);
        document.body.style.userSelect = '';
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onUp);
      };
      // Block text selection while dragging; restored on pointer up.
      setIsDragging(true);
      document.body.style.userSelect = 'none';
      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onUp);
    },
    [isHorizontal, side],
  );

  // Applied imperatively, with `!important`, rather than through the `style`
  // prop — which React cannot mark important.
  //
  // Nearly every sheet sets its own width as `sm:max-w-[920px]!`, because the
  // base `data-[side=right]:sm:max-w-sm` is a more specific variant that
  // tailwind-merge won't dedupe against a plain `sm:` class. That `!important`
  // then beat the inline size from this handle, so the sheet could be dragged
  // narrower but never wider than its configured width — the resize looked
  // broken in one direction.
  React.useLayoutEffect(() => {
    const popup = popupRef.current;
    if (!popup) return;
    const sizeProp = isHorizontal ? 'width' : 'height';
    const maxProp = isHorizontal ? 'max-width' : 'max-height';
    if (size == null) {
      // Never dragged (or reset): let the classes drive the sheet again.
      popup.style.removeProperty(sizeProp);
      popup.style.removeProperty(maxProp);
      return;
    }
    popup.style.setProperty(sizeProp, `${size}px`, 'important');
    popup.style.setProperty(maxProp, `${size}px`, 'important');
  }, [size, isHorizontal]);

  return (
    <SheetPortal>
      <SheetOverlay />
      <SheetPrimitive.Popup
        ref={popupRef}
        data-slot='sheet-content'
        data-side={side}
        className={cn(
          'fixed z-40 flex flex-col bg-popover bg-clip-padding text-xs/relaxed text-popover-foreground shadow-overlay transition duration-300 ease-[cubic-bezier(0.32,0.72,0,1)] motion-reduce:transition-none will-change-transform data-[side=bottom]:inset-x-0 data-[side=bottom]:bottom-0 data-[side=bottom]:h-auto data-[side=bottom]:border-t data-[side=bottom]:data-ending-style:translate-y-full data-[side=bottom]:data-starting-style:translate-y-full data-[side=left]:inset-y-0 data-[side=left]:left-0 data-[side=left]:h-full data-[side=left]:w-3/4 data-[side=left]:border-r data-[side=left]:data-ending-style:-translate-x-full data-[side=left]:data-starting-style:-translate-x-full data-[side=right]:inset-y-0 data-[side=right]:right-0 data-[side=right]:h-full data-[side=right]:w-3/4 data-[side=right]:border-l data-[side=right]:data-ending-style:translate-x-full data-[side=right]:data-starting-style:translate-x-full data-[side=top]:inset-x-0 data-[side=top]:top-0 data-[side=top]:h-auto data-[side=top]:border-b data-[side=top]:data-ending-style:-translate-y-full data-[side=top]:data-starting-style:-translate-y-full data-[side=left]:sm:max-w-[640px] data-[side=right]:sm:max-w-[640px]',
          // Don't animate size changes while actively dragging the handle.
          isDragging && 'transition-none',
          className,
        )}
        {...props}
        style={style as React.CSSProperties}
      >
        {children}
        {resizable && (
          <div
            data-slot='sheet-resize-handle'
            aria-hidden
            onPointerDown={handleResizeStart}
            className={cn(
              'absolute z-50 flex items-center justify-center touch-none',
              isHorizontal
                ? 'inset-y-0 w-2 cursor-col-resize'
                : 'inset-x-0 h-2 cursor-row-resize',
              // Centre the strip on the sheet's edge (not just inside it).
              side === 'right' && 'left-0 -translate-x-1/2',
              side === 'left' && 'right-0 translate-x-1/2',
              side === 'bottom' && 'top-0 -translate-y-1/2',
              side === 'top' && 'bottom-0 translate-y-1/2',
            )}
          >
            {/* Edge line + teal grab pill — mirrors the agentic ResizableHandle
                (`withHandle`) so every resizer in the app looks the same. */}
            <span
              className={cn(
                'absolute bg-border',
                isHorizontal
                  ? 'inset-y-0 left-1/2 w-px -translate-x-1/2'
                  : 'inset-x-0 top-1/2 h-px -translate-y-1/2',
              )}
            />
            <span
              className={cn(
                'relative z-10 shrink-0 rounded-lg bg-primary',
                isHorizontal ? 'h-6 w-1' : 'h-1 w-6',
              )}
            />
          </div>
        )}
        {showCloseButton && (
          <SheetPrimitive.Close
            data-slot='sheet-close'
            render={
              <Button
                variant='ghost'
                className='absolute top-4 right-4'
                size='icon-sm'
              />
            }
          >
            <FontAwesomeIcon icon={faXmark} />
            <span className='sr-only'>Close</span>
          </SheetPrimitive.Close>
        )}
      </SheetPrimitive.Popup>
    </SheetPortal>
  );
}

function SheetHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot='sheet-header'
      className={cn('flex flex-col gap-1.5 p-6', className)}
      {...props}
    />
  );
}

function SheetFooter({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot='sheet-footer'
      className={cn('mt-auto flex flex-col gap-2 p-6', className)}
      {...props}
    />
  );
}

function SheetTitle({ className, ...props }: SheetPrimitive.Title.Props) {
  return (
    <SheetPrimitive.Title
      data-slot='sheet-title'
      className={cn(
        'font-heading text-sm font-medium text-foreground',
        className,
      )}
      {...props}
    />
  );
}

function SheetDescription({
  className,
  ...props
}: SheetPrimitive.Description.Props) {
  return (
    <SheetPrimitive.Description
      data-slot='sheet-description'
      className={cn('text-xs/relaxed text-muted-foreground', className)}
      {...props}
    />
  );
}

export {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
};
